import React from 'react'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import {fullName, TRACKING_STATUS_BADGE, trackingStatusLabel} from '../application/ApplicationModel'
import {formatDateTime} from '../shared/DocumentApi'
import {canAdvance, canGoBack, ITrackingModel} from './TrackingModel'
import {ITagModel} from '../tag/TagModel'
import {TagChips} from '../tag/TagChips'
import {STEP_OUTCOME_LABEL} from '../process-flow/HrProcessStepModel'

type Props = {
  data: ITrackingModel[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onBack: (tracking: ITrackingModel) => void
  onAdvance: (tracking: ITrackingModel) => void
  onDetail: (tracking: ITrackingModel) => void
  onEdit: (tracking: ITrackingModel) => void
  onDelete: (tracking: ITrackingModel) => void
  onTag: (tracking: ITrackingModel) => void
  onInterview: (tracking: ITrackingModel) => void
  onRemoveTag: (tracking: ITrackingModel, tag: ITagModel) => void
  busyId?: string | null
  loading?: boolean
}

export const TrackingTable: React.FC<Props> = ({
  data,
  currentPage,
  totalPages,
  onPageChange,
  onBack,
  onAdvance,
  onDetail,
  onEdit,
  onDelete,
  onTag,
  onInterview,
  onRemoveTag,
  busyId,
  loading = false,
}) => {
  const columns: TableColumn<ITrackingModel>[] = [
    {
      key: 'firstname',
      label: 'Aday',
      render: (_, tracking) => (
        <div className='d-flex flex-column'>
          <span className='fw-bolder text-gray-800'>{fullName(tracking)}</span>
          <span className='text-muted fs-7'>{tracking.email}</span>
          {tracking.phone && <span className='text-muted fs-7'>{tracking.phone}</span>}
          <TagChips tags={tracking.tags} onRemove={(tag) => onRemoveTag(tracking, tag)} />
        </div>
      ),
    },
    {
      key: 'jobTitle',
      label: 'İlan / Süreç',
      render: (_, tracking) => (
        <div className='d-flex flex-column'>
          <span className='fw-semibold'>{tracking.jobTitle}</span>
          <span className='text-muted fs-7'>{tracking.hrProcessName}</span>
          {tracking.assignedUserName && (
            <span className='text-muted fs-8'>
              <i className='bi bi-telephone me-1'></i>
              {tracking.assignedUserName}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'currentStepName',
      label: 'Bulunduğu Adım',
      render: (_, tracking) => (
        <div className='d-flex flex-column gap-1'>
          <span className='fw-semibold'>
            {tracking.currentStepType === 'END' && <i className='bi bi-flag-fill text-success me-1'></i>}
            {tracking.currentStepName || '-'}
          </span>
          {tracking.currentStepMissing && (
            <span className='text-danger fs-8'>Adım süreç akışından silinmiş</span>
          )}
          {!tracking.currentStepMissing && tracking.nextSteps.length > 0 && (
            <span className='text-muted fs-8'>
              Sonraki: {tracking.nextSteps.map((step) => step.name).join(' / ')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Durum',
      width: '150px',
      render: (_, tracking) => (
        <div className='d-flex flex-column gap-1'>
          <span className={`badge ${TRACKING_STATUS_BADGE[tracking.status]} align-self-start`}>
            {trackingStatusLabel(tracking.status)}
          </span>
          {tracking.status === 'COMPLETED' && tracking.outcome && (
            <span
              className={`badge ${tracking.outcome === 'POSITIVE' ? 'badge-success' : 'badge-danger'} align-self-start`}
            >
              {STEP_OUTCOME_LABEL[tracking.outcome]}
            </span>
          )}
          <span className='text-muted fs-8'>{formatDateTime(tracking.updatedDate)}</span>
        </div>
      ),
    },
  ]

  const isBusy = (tracking: ITrackingModel) => busyId === tracking.id

  const actions: TableAction<ITrackingModel>[] = [
    {
      label: 'Geri',
      icon: 'bi bi-chevron-left',
      className: 'btn-light-warning me-2',
      onClick: onBack,
      disabled: (tracking) => isBusy(tracking) || !canGoBack(tracking),
    },
    {
      label: 'İlerle',
      icon: 'bi bi-chevron-right',
      className: 'btn-light-success me-2',
      onClick: onAdvance,
      disabled: (tracking) => isBusy(tracking) || !canAdvance(tracking),
    },
    {
      label: 'Etiketle',
      icon: 'bi bi-tags',
      className: 'btn-light-warning me-2',
      onClick: onTag,
      disabled: (tracking) => !tracking.candidateId,
    },
    {
      label: 'Mülakat',
      icon: 'bi bi-calendar-plus',
      className: 'btn-light-primary me-2',
      onClick: onInterview,
      disabled: (tracking) => tracking.status !== 'IN_PROGRESS',
    },
    {label: 'Detay', icon: 'bi bi-eye', className: 'btn-light-info me-2', onClick: onDetail},
    {label: 'Düzenle', icon: 'bi bi-pencil', className: 'btn-light-primary me-2', onClick: onEdit},
    {label: 'Sil', icon: 'bi bi-trash', className: 'btn-light-danger', onClick: onDelete},
  ]

  return (
    <DataTable
      data={data}
      columns={columns}
      actions={actions}
      showPagination={true}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      loading={loading}
      emptyMessage='Takip edilen aday bulunamadı. Gelen Başvurular ekranından süreç başlatabilirsiniz.'
    />
  )
}
